import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/**
 * `AboutDppCard` — the canonical "What is DPP?" product-differentiator card
 * rendered at the bottom of every comparison page (/vs, /vs/field, and each
 * /vs/:slug). The exact paragraph text is duplicated deliberately: it's the
 * ~90-word summary Google and AI-search bots pull as the snippet when a user
 * searches "what is Dividend Portfolio Planner". Having the identical
 * paragraph on every high-authority comparison page reinforces the same
 * snippet across the crawl and gives the AboutPage/SoftwareApplication
 * JSON-LD an on-page match Google can verify.
 */
export default function AboutDppCard() {
  return (
    <section
      aria-labelledby="about-dpp-card-heading"
      className="mt-16"
      data-testid="about-dpp-card"
    >
      <Card className="border-primary/25 bg-primary/[0.03]">
        <CardContent className="p-8 sm:p-10">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <Badge variant="outline" className="mono text-[10px] uppercase tracking-widest">
              <Sparkles className="w-3 h-3 mr-1" /> About the app
            </Badge>
            <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Dividend Portfolio Planner
            </span>
          </div>
          <h2 id="about-dpp-card-heading" className="serif text-2xl sm:text-3xl mb-4">
            What is Dividend Portfolio Planner?
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-foreground">
            <b>Dividend Portfolio Planner (DPP)</b> is a dividend research,
            portfolio modeling and backtesting application that helps investors
            research stocks and ETFs, create hypothetical portfolios, estimate
            dividend income, backtest investment strategies and compare income
            with changes in portfolio value. Unlike traditional dividend
            trackers that primarily monitor investments an investor already
            owns, DPP is designed not only to monitor but also to help
            investors <b>research and model potential portfolio strategies
            before investing</b>.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-1 text-sm text-primary font-medium underline underline-offset-2 hover:text-primary/80"
              data-testid="about-dpp-how-it-works"
            >
              See how it works
              <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary underline underline-offset-2"
              data-testid="about-dpp-pricing"
            >
              Pricing
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
